<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2022 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { __ } from '@oat-sa-private/ui-core';
    import { Icon } from '@oat-sa-private/ui-elements';
    import Player from '../player/Player.svelte';

    /**
     * @property {number} [duration] maximum duration of recording (ms)
     * @property {string} [deviceId=default] deviceId from mediaDevices.enumerateDevices()
     * @fires start {CustomEvent} Fires when audio starts to record.
     * @fires error {CustomEvent} Fires when recording error occur.
     * @fires finish {CustomEvent} Fires when recording is done.
     * @fires delete {CustomEvent} Fires when delete button of Player is clicked.
     * @fires audiofinish {CustomEvent} Fires when playing is finished.
     */
    export let duration;
    export let deviceId;

    let mediaRecorder;
    let recording = false;
    let src = null;
    let stopTimer;
    let recordingChunks = [];

    const dispatch = createEventDispatcher();

    const buttonLabelStartRecording = __('Start recording');
    const buttonLabelStopRecording = __('Stop recording');
    const recordingLabel = __('Recording...');
    const type = 'audio/mp3';

    $: statusLabel = recording ? recordingLabel : '';

    /**
     * Hander for start recording button
     * Checks the navigator.mediaDevices and getUserMedia are supported
     * @fires error {CustomEvent} error is browser doesnt support mediaDevices or getUserMedia
     */
    function startRecording() {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            const constraints = {
                audio: true
            };
            if (deviceId) {
                constraints.audio = { deviceId };
            }
            navigator.mediaDevices
                .getUserMedia(constraints)
                .then(stream => {
                    window.stream = stream;
                    //create mediaRecorder instance and setup event listeners
                    mediaRecorder = new MediaRecorder(stream, { type });
                    mediaRecorder.ondataavailable = mediaRecorderDataAvailable;
                    mediaRecorder.onstart = mediaRecorderStart;
                    mediaRecorder.onerror = mediaRecorderError;
                    mediaRecorder.onstop = mediaRecorderStop;

                    mediaRecorder.start();
                })
                .catch(err => {
                    mediaRecorderError(err);
                });
        } else {
            mediaRecorderError(new Error('getUserMedia not supported on your browser'));
        }
    }

    function stopRecording() {
        mediaRecorder.stop();
    }

    function mediaRecorderStart() {
        dispatch('start');
        recording = true;
        if (duration) {
            stopTimer = setTimeout(stopRecording, duration);
        }
    }

    function mediaRecorderDataAvailable(e) {
        recordingChunks.push(e.data);
    }

    function mediaRecorderError(err) {
        dispatch('error', err);
    }

    function mediaRecorderStop() {
        clearTimeout(stopTimer);
        dispatchRecordingFinished();
        recording = false;
    }

    function dispatchRecordingFinished() {
        const blob = new Blob(recordingChunks, { type });
        src = window.URL.createObjectURL(blob);
        recordingChunks = [];
        dispatch('finish');
    }
    function handleDelete() {
        src = null;
        dispatch('delete');
    }
    function handleAudioFinish() {
        dispatch('audiofinish');
    }
    function handleAudioStart() {
        dispatch('play');
    }
</script>

<style>
    .recorder {
        width: 100%;
        height: 6.5rem;
        display: flex;
        justify-content: flex-start;
        border: var(--border-medium) solid var(--color-border-default);

        & button {
            border: 0;
            width: 18rem;
            color: var(--color-text-default);
            display: flex;
            flex: 0 0 18rem;
            justify-content: center;
            align-items: center;
            gap: 0.5rem;
            background-color: var(--color-bg-default);
            text-transform: uppercase;
            font-family: var(--font-ui);
            font-size: var(--fontsize-body-xs);
            white-space: nowrap;
        }

        & .recording-status-container {
            display: flex;
            flex: 1 0 auto;
            justify-content: center;
            align-items: center;
            border-style: solid;
            border-block-width: 0;
            border-inline-width: var(--border-medium) 0;
            border-color: var(--color-border-default);

            &.recording {
                background-color: var(--color-text-default);
                color: var(--color-text-inverted);
            }
        }
    }
    @media screen and (--mq-minwidth-massive) {
        .recorder {
            border-width: 2px;
        }
    }
</style>

<div>
    {#if !src}
        <div class="recorder">
            {#if recording}
                <button class="stop-recording" on:click={stopRecording}>
                    <Icon name="stop-16" />
                    <div>{buttonLabelStopRecording}</div>
                </button>
            {:else}
                <button class="start-recording" on:click={startRecording}>
                    <Icon name="notepad-16" />
                    <div>{buttonLabelStartRecording}</div>
                </button>
            {/if}
            <div class="recording-status-container" class:recording><div>{statusLabel}</div></div>
        </div>
    {:else}
        <Player
            on:delete={handleDelete}
            on:finish={handleAudioFinish}
            on:error
            on:start={handleAudioStart}
            {src}
            {type}
            autostart={false}
            startTime={0}
            deleteButton={true}
            allowVolumeControls={false} />
    {/if}
</div>
